import React, { useEffect, useState } from "react";
import Link from "next/link";
import Meta from "../Meta";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import Loader from "../loader";
import ApiService from "../../../services/ApiService";
import { applyLang } from "../../../i18n";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { resolveImage } from "../utils";

const styles = `
.t26-galeries-page {
  padding: 0 clamp(16px, 4vw, 32px) clamp(40px, 6vw, 80px);
  max-width: var(--container);
  margin-inline: auto;
}
.t26-galeries-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: clamp(14px, 2vw, 20px);
}
.t26-galerie-card {
  position: relative;
  aspect-ratio: 4 / 5;
  border-radius: var(--r-lg);
  overflow: hidden;
  background: color-mix(in oklab, var(--primary) 18%, var(--bg-2));
  display: block;
}
.t26-galerie-card img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .4s ease;
}
.t26-galerie-card:hover img { transform: scale(1.06); }
.t26-galerie-card::after {
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(180deg, transparent 50%, rgba(8,14,22,.78) 100%);
}
.t26-galerie-card__caption {
  position: absolute; z-index: 2;
  inset: auto 0 0 0;
  padding: 18px 20px;
  color: white;
}
.t26-galerie-card__title {
  font-family: var(--font-display);
  font-size: 18px; font-weight: 700;
  line-height: 1.2;
  text-shadow: 0 1px 4px rgba(0,0,0,.4);
}
.t26-galerie-card__sub {
  display: block;
  margin-top: 4px;
  font-size: 12px; font-weight: 500;
  opacity: .9;
}

.t26-galeries-loadmore {
  display: flex; justify-content: center; margin-top: 32px;
}
`;

export default function Galeries({ context, loading }) {
    const { org, lang, langValue } = context;
    const [data, setData] = useState([]);
    const [busy, setBusy] = useState(true);
    const [page, setPage] = useState(0);
    const [finished, setFinished] = useState(false);

    const fetchPage = async (init = false) => {
        if (!org?.id) { setBusy(false); return; }
        const nextPage = init ? 1 : page + 1;
        setBusy(true);
        try {
            const res = await ApiService.post("/galeries/" + org.id, { page: nextPage });
            const list = Array.isArray(res?.data) ? res.data : [];
            setData(prev => (init ? list : [...prev, ...list]));
            setPage(nextPage);
            setFinished(list.length === 0);
        } catch (e) {
            setFinished(true);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        fetchPage(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org?.id]);

    if (loading) return <Loader />;

    const galeries = data.map(g => applyLang(g, lang));

    return (
        <>
            <Meta context={context} title={t`Galeries`} description={t`Galeries photo de ${org.name}`} />
            <style dangerouslySetInnerHTML={{ __html: styles }} />

            <PageHeader
                title={t`Galeries`}
                titleEm={t`photos`}
                subtitle={t`Revivez en images les moments forts de notre communauté`}
                crumbs={[
                    { label: t`Accueil`, url: org.BASE_WEB_URL },
                    { label: t`Galeries` },
                ]}
            />

            <div className="t26-galeries-page">
                {!busy && galeries.length === 0 ? (
                    <EmptyState
                        icon="IconPhotoOff"
                        title={t`Aucune galerie pour le moment`}
                        subtitle={t`De nouveaux albums photos seront publiés ici très prochainement.`}
                    />
                ) : (
                    <>
                        <div className="t26-galeries-page__grid">
                            {galeries.map(g => {
                                const cover = g.bgImage || g.image || null;
                                const url = `${org.BASE_WEB_URL}/galerie/${g.id || g._id}`;
                                const dateLabel = g.datetime
                                    ? new Date(g.datetime).toLocaleDateString(langValue || "fr", { day: "numeric", month: "long", year: "numeric" })
                                    : null;
                                return (
                                    <Link key={g.id || g._id} href={url} legacyBehavior>
                                        <a className="t26-galerie-card">
                                            {cover && <img src={resolveImage(cover)} alt={g.name || ""} loading="lazy" />}
                                            <div className="t26-galerie-card__caption">
                                                <span className="t26-galerie-card__title">{g.name}</span>
                                                {dateLabel && <span className="t26-galerie-card__sub">{dateLabel}</span>}
                                            </div>
                                        </a>
                                    </Link>
                                );
                            })}
                        </div>

                        {!finished && (
                            <div className="t26-galeries-loadmore">
                                <button
                                    className="tr-btn tr-btn--ghost tr-btn--md tr-focusable"
                                    onClick={() => fetchPage(false)}
                                    disabled={busy}
                                >
                                    {busy ? <Trans>Chargement…</Trans> : <Trans>Charger plus</Trans>}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
